import { supabase } from '@/integrations/supabase/client';
import type { Json, Database } from '@/integrations/supabase/types';
import type {
  OrdemServico,
  StatusOrdemServico,
  ServicoOS,
  EnderecoOS,
  TipoEventoOS,
} from '@/types/ordemServico';
import { parseServicos, parseEndereco } from './ordens-servico.normalizers';
import { resolveOSIndicator, type OSIndicator, type OSRow } from '@/lib/os-indicator';
import { statusInicialOS } from '@/lib/os-status-invariant';

export interface OSFinalizacaoResumo {
  data_inicio_execucao: string | null;
  data_fim_execucao: string | null;
  tempo_servico_minutos: number | null;
  valor_total: number;
}

type ItemOSRow = Database['public']['Tables']['ordem_servico_itens']['Row'];

export type ItemOSWithServico = ItemOSRow & {
  servico: { id: string; tipo_preco: string; valor_base: number } | null;
};

export async function fetchOSResumoFinalizacao(osId: string): Promise<OSFinalizacaoResumo> {
  const { data, error } = await supabase
    .from('ordens_servico')
    .select('data_inicio_execucao, data_fim_execucao, tempo_servico_minutos, valor_total')
    .eq('id', osId)
    .single();
  if (error) throw error;
  return data as OSFinalizacaoResumo;
}

export async function fetchItensOSComServico(osId: string): Promise<ItemOSWithServico[]> {
  const { data, error } = await supabase
    .from('ordem_servico_itens')
    .select(`
      *,
      servico:servicos(id, tipo_preco, valor_base)
    `)
    .eq('ordem_servico_id', osId);
  if (error) throw error;
  return (data ?? []) as unknown as ItemOSWithServico[];
}


export async function uploadComprovativoPagamento(params: {
  osId: string;
  file: File;
}): Promise<string> {
  const filename = `${params.osId}/${Date.now()}-comprovativo-${params.file.name}`;
  const { error } = await supabase.storage
    .from('comprovativos-pagamento')
    .upload(filename, params.file);
  if (error) throw error;
  const { data } = supabase.storage.from('comprovativos-pagamento').getPublicUrl(filename);
  return data.publicUrl;
}

export async function uploadFotoServico(params: {
  osId: string;
  file: File;
}): Promise<string | null> {
  const filename = `${params.osId}/${Date.now()}-${params.file.name}`;
  const { error } = await supabase.storage
    .from('fotos-servico')
    .upload(filename, params.file);
  if (error) {
    console.error('Erro ao fazer upload de foto:', error);
    return null;
  }
  const { data } = supabase.storage.from('fotos-servico').getPublicUrl(filename);
  return data.publicUrl;
}

export async function finalizarOS(params: {
  osId: string;
  tempoServicoMinutos: number;
  dataFim: Date;
  fotosUrls: string[];
  observacoes: string;
  comprovativoUrl?: string | null;
  valorTotal: number;
}): Promise<void> {
  const update: Record<string, unknown> = {
    status: 'concluida',
    tempo_servico_minutos: params.tempoServicoMinutos,
    data_fim_execucao: params.dataFim.toISOString(),
    fotos_servico: params.fotosUrls as unknown as Json,
    observacoes_finalizacao: params.observacoes,
    valor_total: params.valorTotal,
  };
  // Só atualiza o comprovativo quando há um novo (preserva o da 1ª conclusão
  // numa OS reaberta, onde não se reenvia comprovante).
  if (params.comprovativoUrl) {
    update.comprovativo_pagamento_url = params.comprovativoUrl;
  }

  const { error } = await supabase
    .from('ordens_servico')
    .update(update)
    .eq('id', params.osId);
  if (error) throw error;
}

export async function atualizarItemOSPorHora(params: {
  itemId: string;
  quantidade: number;
  valorTotal: number;
}): Promise<void> {
  const { error } = await supabase
    .from('ordem_servico_itens')
    .update({
      quantidade: params.quantidade,
      valor_total: params.valorTotal,
    })
    .eq('id', params.itemId);
  if (error) throw error;
}

export async function registrarEventoOS(params: {
  osId: string;
  orgId: string;
  tipo: TipoEventoOS;
  descricao: string;
  detalhes: Record<string, unknown>;
}): Promise<void> {
  const { data: profileId } = await supabase.rpc('get_my_profile_id');
  const { error } = await supabase.from('eventos_ordem_servico').insert({
    organization_id: params.orgId,
    ordem_servico_id: params.osId,
    usuario_id: profileId as string,
    tipo: params.tipo,
    descricao: params.descricao,
    detalhes: params.detalhes as unknown as Json,
  });
  if (error) throw error;
}

export interface FiltrosOS {
  status?: StatusOrdemServico[];
  prestador_id?: string;
  responsavel_id?: string;
  cliente_id?: string;
}

export async function listOrdensServico(filtros?: FiltrosOS): Promise<OrdemServico[]> {
  let query = supabase
    .from('ordens_servico')
    .select(`
      *,
      cliente:clientes_potenciais(id, codigo_cliente, nome, telefone, email, avatar_url, endereco_rua, endereco_numero, endereco_cidade, endereco_codigo_postal, endereco_pais),
      prestador:prestadores_servico(id, profile_id, telefone, servicos_oferecidos, profile:profile_id(id, nome, avatar_url, telefone)),
      responsavel:profiles!ordens_servico_responsavel_id_fkey(id, nome, avatar_url)
    `)
    .order('criado_em', { ascending: false });

  if (filtros?.status && filtros.status.length > 0) {
    query = query.in('status', filtros.status);
  }
  if (filtros?.prestador_id) {
    query = query.eq('prestador_id', filtros.prestador_id);
  }
  if (filtros?.responsavel_id) {
    query = query.eq('responsavel_id', filtros.responsavel_id);
  }
  if (filtros?.cliente_id) {
    query = query.eq('cliente_id', filtros.cliente_id);
  }

  const { data, error } = await query;
  if (error) throw error;

  return (data ?? []).map((os: Record<string, unknown>) => ({
    ...os,
    servicos: parseServicos(os.servicos as Json),
    endereco: parseEndereco(os.endereco as Json),
  })) as OrdemServico[];
}

export async function listOSDisponiveis(): Promise<OrdemServico[]> {
  const { data, error } = await supabase
    .from('ordens_servico')
    .select(`
      *,
      cliente:clientes_potenciais(id, codigo_cliente, nome, telefone, email, avatar_url, endereco_rua, endereco_numero, endereco_cidade, endereco_codigo_postal, endereco_pais),
      responsavel:profiles!ordens_servico_responsavel_id_fkey(id, nome, avatar_url)
    `)
    .is('prestador_id', null)
    .eq('status', 'pendente')
    .order('data_agendada', { ascending: true });
  if (error) throw error;

  return (data ?? []).map((os: Record<string, unknown>) => ({
    ...os,
    servicos: parseServicos(os.servicos as Json),
    endereco: parseEndereco(os.endereco as Json),
  })) as OrdemServico[];
}

export async function getOrdemServicoById(id: string): Promise<OrdemServico> {
  const { data, error } = await supabase
    .from('ordens_servico')
    .select(`
      *,
      cliente:clientes_potenciais!ordens_servico_cliente_id_fkey(id, codigo_cliente, nome, telefone, email, avatar_url),
      prestador:prestadores_servico!ordens_servico_prestador_id_fkey(
        id,
        telefone,
        profile:profiles!prestadores_servico_profile_id_fkey(id, nome, telefone)
      ),
      responsavel:profiles!ordens_servico_responsavel_id_fkey(id, nome)
    `)
    .eq('id', id)
    .single();
  if (error) throw error;

  return {
    ...data,
    servicos: parseServicos(data.servicos as Json),
    endereco: parseEndereco(data.endereco as Json),
  } as OrdemServico;
}

export interface AutoatribuirResult {
  id: string;
  numero_os: string;
}

export async function autoatribuirOS(osId: string): Promise<AutoatribuirResult> {
  const { data, error } = await supabase.rpc('autoatribuir_os', { _os_id: osId });
  if (error) throw error;
  if (!data || (Array.isArray(data) && data.length === 0)) {
    throw new Error('NO_ROW_RETURNED');
  }
  const row = Array.isArray(data) ? data[0] : data;
  return row as AutoatribuirResult;
}

export interface CriarOSInput {
  orgId: string;
  cliente_id?: string;
  prestador_id?: string | null;
  responsavel_id?: string;
  servicos?: ServicoOS[];
  data_agendada?: string;
  endereco?: EnderecoOS;
  valor_total?: number;
  observacoes?: string;
}

export async function criarOrdemServico(input: CriarOSInput): Promise<{ id: string; numero_os: string }> {
  const { data: os, error } = await supabase
    .from('ordens_servico')
    .insert([{
      organization_id: input.orgId,
      numero_os: '',
      // Invariante prestador↔status (espelha o trigger trg_os_aceitar_ao_atribuir_prestador):
      // criar já com prestador nasce 'aceita', senão a OS ficava invisível ao prestador.
      status: statusInicialOS(input.prestador_id),
      cliente_id: input.cliente_id ?? null,
      prestador_id: input.prestador_id ?? null,
      responsavel_id: input.responsavel_id ?? null,
      servicos: (input.servicos ?? []) as unknown as Json,
      data_agendada: input.data_agendada ?? null,
      endereco: (input.endereco ?? {}) as unknown as Json,
      valor_total: input.valor_total ?? 0,
      observacoes: input.observacoes ?? '',
    }])
    .select('id, numero_os')
    .single();
  if (error) throw error;

  await registrarEventoOS({
    osId: os.id,
    orgId: input.orgId,
    tipo: 'os_criada',
    descricao: `Ordem de serviço ${os.numero_os} criada`,
    detalhes: { cliente_id: input.cliente_id, prestador_id: input.prestador_id },
  });

  return os;
}

export type AtualizarOSInput = Partial<{
  cliente_id: string | null;
  prestador_id: string | null;
  responsavel_id: string | null;
  servicos: ServicoOS[];
  data_agendada: string | null;
  endereco: EnderecoOS;
  valor_total: number;
  observacoes: string;
  observacoes_internas: string;
}>;

export async function atualizarOrdemServico(id: string, dados: AtualizarOSInput, orgId: string): Promise<void> {
  const updateData: Record<string, unknown> = {};
  if (dados.cliente_id !== undefined) updateData.cliente_id = dados.cliente_id;
  if (dados.prestador_id !== undefined) updateData.prestador_id = dados.prestador_id;
  if (dados.responsavel_id !== undefined) updateData.responsavel_id = dados.responsavel_id;
  if (dados.servicos !== undefined) updateData.servicos = dados.servicos as unknown as Json;
  if (dados.data_agendada !== undefined) updateData.data_agendada = dados.data_agendada;
  if (dados.endereco !== undefined) updateData.endereco = dados.endereco as unknown as Json;
  if (dados.valor_total !== undefined) updateData.valor_total = dados.valor_total;
  if (dados.observacoes !== undefined) updateData.observacoes = dados.observacoes;
  if (dados.observacoes_internas !== undefined) updateData.observacoes_internas = dados.observacoes_internas;

  const { error } = await supabase
    .from('ordens_servico')
    .update(updateData)
    .eq('id', id);
  if (error) throw error;

  // Histórico: registar a edição (sem isto, alterar pelo form não deixava log).
  await registrarEventoOS({
    osId: id,
    orgId,
    tipo: 'os_editada',
    descricao: 'Ordem de serviço editada',
    detalhes: { campos_alterados: Object.keys(updateData) },
  });
}

export interface AtualizarStatusInput {
  id: string;
  orgId: string;
  status: StatusOrdemServico;
  observacao?: string;
  prestador_id?: string | null;
}

export async function atualizarStatusOS(input: AtualizarStatusInput): Promise<{ numero_os: string }> {
  const updateData: Record<string, unknown> = { status: input.status };
  if (input.prestador_id !== undefined) {
    updateData.prestador_id = input.prestador_id;
  }

  const { data: os, error } = await supabase
    .from('ordens_servico')
    .update(updateData)
    .eq('id', input.id)
    .select('numero_os')
    .single();
  if (error) throw error;

  const tipoEvento =
    input.status === 'concluida' ? 'os_concluida' :
    input.status === 'cancelada' ? 'os_cancelada' :
    input.status === 'aceita' ? 'os_aceita' :
    input.status === 'recusada' ? 'os_recusada' :
    input.prestador_id === null ? 'prestador_alterado' :
    'status_alterado';

  const descricao = input.prestador_id === null
    ? 'Prestador removido da OS'
    : `Status alterado para ${input.status}`;

  await registrarEventoOS({
    osId: input.id,
    orgId: input.orgId,
    tipo: tipoEvento,
    descricao,
    detalhes: {
      status_anterior: null,
      novo_status: input.status,
      observacao: input.observacao,
      prestador_removido: input.prestador_id === null,
    },
  });

  return os;
}

export async function iniciarExecucaoOS(params: {
  id: string;
  orgId: string;
}): Promise<{ numero_os: string }> {
  const { data: os, error } = await supabase
    .from('ordens_servico')
    .update({
      status: 'em_execucao',
      data_inicio_execucao: new Date().toISOString(),
    })
    .eq('id', params.id)
    .select('numero_os')
    .single();
  if (error) throw error;

  await registrarEventoOS({
    osId: params.id,
    orgId: params.orgId,
    tipo: 'status_alterado',
    descricao: 'Execução iniciada',
    detalhes: { novo_status: 'em_execucao' },
  });

  return os;
}

export interface TransferirOSInput {
  id: string;
  orgId: string;
  prestador_id?: string;
  responsavel_id?: string;
}

export async function transferirOS(input: TransferirOSInput): Promise<{ numero_os: string }> {
  const update: Record<string, string | null> = {};
  if (input.prestador_id !== undefined) update.prestador_id = input.prestador_id || null;
  if (input.responsavel_id !== undefined) update.responsavel_id = input.responsavel_id || null;

  const { data: os, error } = await supabase
    .from('ordens_servico')
    .update(update)
    .eq('id', input.id)
    .select('numero_os')
    .single();
  if (error) throw error;

  const tipoEvento: TipoEventoOS = input.prestador_id !== undefined
    ? 'prestador_alterado'
    : 'responsavel_alterado';

  await registrarEventoOS({
    osId: input.id,
    orgId: input.orgId,
    tipo: tipoEvento,
    descricao: `${input.prestador_id !== undefined ? 'Prestador' : 'Responsável'} alterado`,
    detalhes: { prestador_id: input.prestador_id, responsavel_id: input.responsavel_id },
  });

  return os;
}

export interface AdicionarObservacaoInput {
  id: string;
  orgId: string;
  observacao: string;
  interna?: boolean;
}

export async function adicionarObservacaoOS(input: AdicionarObservacaoInput): Promise<void> {
  const { data: osAtual, error: fetchError } = await supabase
    .from('ordens_servico')
    .select('observacoes, observacoes_internas')
    .eq('id', input.id)
    .single();
  if (fetchError) throw fetchError;

  const interna = input.interna ?? false;
  const campo = interna ? 'observacoes_internas' : 'observacoes';
  const valorAtual = interna ? osAtual.observacoes_internas : osAtual.observacoes;
  const stamp = `[${new Date().toLocaleString('pt-BR')}] ${input.observacao}`;
  const novoValor = valorAtual ? `${valorAtual}\n\n${stamp}` : stamp;

  const { error } = await supabase
    .from('ordens_servico')
    .update({ [campo]: novoValor })
    .eq('id', input.id);
  if (error) throw error;

  await registrarEventoOS({
    osId: input.id,
    orgId: input.orgId,
    tipo: 'observacao_adicionada',
    descricao: interna ? 'Observação interna adicionada' : 'Observação adicionada',
    detalhes: { observacao: input.observacao, interna },
  });
}

const REAGENDAR_BLOCKED_STATUSES = ['em_execucao', 'concluida', 'cancelada', 'recusada', 'excluida'] as const;

export class ReagendarOSBlockedStatusError extends Error {
  constructor(public status: string) {
    super(`OS com status "${status}" não pode ser remarcada.`);
    this.name = 'ReagendarOSBlockedStatusError';
  }
}

export interface ReagendarOSInput {
  id: string;
  orgId: string;
  novaData: string | null;
  prestadorId?: string | null;
  duracaoEstimadaMinutos?: number;
  motivo: string;
}

export async function reagendarOS(input: ReagendarOSInput): Promise<{ data_agendada: string | null; numero_os: string }> {
  const { data: osAtual, error: fetchError } = await supabase
    .from('ordens_servico')
    .select('data_agendada, numero_os, status, prestador_id')
    .eq('id', input.id)
    .single();
  if (fetchError) throw fetchError;

  if ((REAGENDAR_BLOCKED_STATUSES as readonly string[]).includes(osAtual.status)) {
    throw new ReagendarOSBlockedStatusError(osAtual.status);
  }

  const updatePayload: Record<string, unknown> = { data_agendada: input.novaData };
  if (input.prestadorId !== undefined) {
    updatePayload.prestador_id = input.prestadorId;
    if (input.prestadorId !== null && osAtual.status === 'pendente') {
      updatePayload.status = 'aceita';
    } else if (
      input.prestadorId === null &&
      osAtual.status === 'aceita' &&
      osAtual.prestador_id !== null
    ) {
      // Mirror of pendente→aceita: unassigning an accepted OS reverts it to the queue.
      updatePayload.status = 'pendente';
    }
  }
  if (input.duracaoEstimadaMinutos !== undefined) {
    updatePayload.duracao_estimada_minutos = input.duracaoEstimadaMinutos;
  }

  // Conditional UPDATE: refuses to mutate if the status changed to a blocked one
  // between the SELECT above and here (prevents race-condition bypass of the guard).
  const { error } = await supabase
    .from('ordens_servico')
    .update(updatePayload)
    .eq('id', input.id)
    .not('status', 'in', `(${REAGENDAR_BLOCKED_STATUSES.join(',')})`);
  if (error) throw error;

  await registrarEventoOS({
    osId: input.id,
    orgId: input.orgId,
    tipo: 'os_reagendada',
    descricao: `OS reagendada: ${input.motivo}`,
    detalhes: {
      data_anterior: osAtual?.data_agendada,
      nova_data: input.novaData,
      prestador_anterior: osAtual?.prestador_id,
      novo_prestador: input.prestadorId ?? null,
      motivo: input.motivo,
    },
  });

  return { data_agendada: osAtual.data_agendada, numero_os: osAtual.numero_os };
}

export interface ExcluirOSInput {
  id: string;
  orgId: string;
  motivo: string;
}

export async function excluirOS(input: ExcluirOSInput): Promise<{ numero_os: string; status: string }> {
  const { data: osAtual, error: fetchError } = await supabase
    .from('ordens_servico')
    .select('status, numero_os, cliente_id, prestador_id, valor_total, servicos, endereco')
    .eq('id', input.id)
    .single();
  if (fetchError) throw fetchError;

  const { data: profileId } = await supabase.rpc('get_my_profile_id');
  const { data: profile } = await supabase
    .from('profiles')
    .select('nome')
    .eq('id', profileId as string)
    .single();

  await supabase.from('auditoria_exclusoes').insert({
    organization_id: input.orgId,
    entidade: 'ordens_servico',
    entidade_id: input.id,
    dados_excluidos: {
      numero_os: osAtual.numero_os,
      status: osAtual.status,
      cliente_id: osAtual.cliente_id,
      prestador_id: osAtual.prestador_id,
      valor_total: osAtual.valor_total,
      servicos: osAtual.servicos,
      endereco: osAtual.endereco,
    } as unknown as Json,
    usuario_id: profileId as string,
    usuario_nome: profile?.nome || 'Usuário',
    motivo: input.motivo,
  });

  await supabase
    .from('ordem_servico_itens')
    .delete()
    .eq('ordem_servico_id', input.id);

  await supabase
    .from('eventos_ordem_servico')
    .delete()
    .eq('ordem_servico_id', input.id);

  const { error } = await supabase
    .from('ordens_servico')
    .delete()
    .eq('id', input.id);
  if (error) throw error;

  return { numero_os: osAtual.numero_os, status: osAtual.status };
}

export interface ReabrirOSInput {
  id: string;
  orgId: string;
  motivo?: string;
}

export class ReabrirOSInvalidStatusError extends Error {
  constructor(public statusAtual: string) {
    super(`INVALID_STATUS:${statusAtual}`);
    this.name = 'ReabrirOSInvalidStatusError';
  }
}

export async function reabrirOS(input: ReabrirOSInput): Promise<{ status: string; numero_os: string }> {
  const { data: osAtual, error: fetchError } = await supabase
    .from('ordens_servico')
    .select('status, numero_os, prestador_id')
    .eq('id', input.id)
    .single();
  if (fetchError) throw fetchError;

  if (!['concluida', 'cancelada'].includes(osAtual.status)) {
    throw new ReabrirOSInvalidStatusError(osAtual.status);
  }

  // Invariante: uma OS com prestador atribuído nunca fica 'pendente' (senão
  // desaparece para o prestador). Reabrir uma OS já atribuída volta-a a 'aceita';
  // sem prestador, volta a 'pendente' (fila de disponíveis).
  const novoStatus: StatusOrdemServico = osAtual.prestador_id ? 'aceita' : 'pendente';

  const { error } = await supabase
    .from('ordens_servico')
    .update({ status: novoStatus })
    .eq('id', input.id);
  if (error) throw error;

  await registrarEventoOS({
    osId: input.id,
    orgId: input.orgId,
    tipo: 'os_reaberta',
    descricao: `OS reaberta de "${osAtual.status}" para "${novoStatus}"`,
    detalhes: {
      status_anterior: osAtual.status,
      novo_status: novoStatus,
      motivo: input.motivo,
    },
  });

  return osAtual;
}

/**
 * Resolve the estimated duration of an OS in minutes.
 * Priority: per-OS override → sum of catalog durations (weighted by quantidade) → 60.
 */
export function resolveOSDuration(os: {
  duracao_estimada_minutos: number | null;
  itens?: { duracao_padrao_minutos: number | null; quantidade: number }[];
}): number {
  if (os.duracao_estimada_minutos != null) return os.duracao_estimada_minutos;
  if (os.itens?.length) {
    const sum = os.itens.reduce((acc, item) => {
      const d = item.duracao_padrao_minutos;
      return d != null ? acc + d * item.quantidade : acc;
    }, 0);
    if (sum > 0) return sum;
  }
  return 60;
}

/**
 * Para uma lista de clienteIds, devolve o indicador da OS mais ativa de cada um.
 * UMA query a ordens_servico (RLS filtra por org), agrupada client-side — espelha
 * o padrão de listInboxPreviews. Clientes sem OS relevante ficam fora do mapa.
 */
export async function listOSIndicadorPorCliente(
  clienteIds: string[],
): Promise<Record<string, OSIndicator>> {
  if (clienteIds.length === 0) return {};

  const { data, error } = await supabase
    .from('ordens_servico')
    .select('cliente_id, status, prestador_id, atualizado_em')
    .in('cliente_id', clienteIds);

  if (error) throw error;

  const porCliente = new Map<string, OSRow[]>();
  for (const row of data ?? []) {
    const id = row.cliente_id;
    if (!id) continue;
    // status from the DB enum can include 'excluida' (legacy) which is not in
    // StatusOrdemServico; resolveOSIndicator will simply skip unknown statuses
    // via the set membership checks (none match → falls through to null).
    const osRow: OSRow = {
      status: row.status as StatusOrdemServico,
      prestador_id: row.prestador_id ?? null,
      atualizado_em: row.atualizado_em ?? null,
    };
    const rows = porCliente.get(id) ?? [];
    rows.push(osRow);
    porCliente.set(id, rows);
  }

  const map: Record<string, OSIndicator> = {};
  for (const [id, rows] of porCliente) {
    const ind = resolveOSIndicator(rows);
    if (ind) map[id] = ind;
  }
  return map;
}
